// Default class names for modal states
const CLASS_NAME = {
  MODAL: 'modal',
  OVERLAY: 'modal__overlay',
  SHOW: 'modal--show',
  OPEN: 'modal__open',
  CLOSE: 'modal__close'
};

import * as customEvent from './events';

export const DEFAULT_OPTIONS = {
  className: CLASS_NAME
};

class Modal {
  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node, to add modal properties on.
   * @param {Object} options - Configuration options, defaults to `DEFAULT_OPTIONS`.
   * */
  constructor(elementRef, options = DEFAULT_OPTIONS) {
    // Initialise attributes

    // Root modal container
    this.elementRef = elementRef;
    // Object to hold all the className of the modal
    this.className = options.className || CLASS_NAME;
    // Button to show the modal
    this.showButton = this._getOpenButton();
    // Button to close the modal
    this.closeButton = this._getCloseButton();

    // Bind functions
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.overlayClickHandler = this.overlayClickHandler.bind(this);

    this._setup();
    this._addEventListeners();
  }

  // Event Handlers

  /**
   * Event handler to open the modal.
   *
   * @param {Event} event - click event on the show button `this.showButton`.
   * */
  show(event) {
    event.preventDefault();

    this.elementRef.classList.add(this.className['SHOW']);
    document.body.classList.add(this.className['OPEN']);

    this.elementRef.classList.remove(this.className['CLOSE']);

    setTimeout(() => {
      this.elementRef.dispatchEvent(new CustomEvent(customEvent.SHOWED_MODAL));
    }, 300);
    setTimeout(() => {
      this.elementRef.setAttribute('style', 'display: block;');
    }, 100);
  }

  /**
   * Event handler to fire when the modal has been made visible to the user.
   *
   * @param {Function} callback - The callback function.
   * */
  onShowed(callback) {
    return this.elementRef.addEventListener(customEvent.SHOWED_MODAL, (event) => {
      callback(event);
    });
  }

  /**
   * Event handler to hide the modal.
   *
   * @param {Event} event - click event on the close button `this.closeButton`.
   * */
  hide(event) {
    event.preventDefault();

    this.elementRef.classList.remove(this.className['SHOW']);
    document.body.classList.remove(this.className['OPEN']);

    this.elementRef.classList.add(this.className['CLOSE']);

    setTimeout(() => {
      this.elementRef.dispatchEvent(new CustomEvent(customEvent.CLOSED_MODAL));
    }, 300);
  }

  /**
   * Event handler to fire when the modal has finished being hidden from the user.
   *
   * @param {Function} callback - The callback function.
   * */
  onHidden(callback) {
    return this.elementRef.addEventListener(customEvent.CLOSED_MODAL, (event) => {
      callback(event);
    });
  }

  /**
   * Click event handler on modal overlay.
   *  Iterates over all modals in the page.
   *  If any modal is NOT dismissible and is already open, do not trigger modal hide action.
   *
   * @param {Event} event - click event on the overlay `this.overlay`.
   * */
  overlayClickHandler(event) {
    let modals = document.querySelectorAll(`.${CLASS_NAME['MODAL']}`);

    modals.forEach(modal => {
      let isDismissible = modal.dataset.dismissible !== 'false';

      if (isDismissible) {
        modal.querySelector("[data-hide='modal']").click();
      }
    });
  }

  // private

  /**
   * Set-up the modal.
   *  1) Prepares all the DOM Nodes needed for the modal.
   *  2) Appropriately binds all necessary event callbacks.
   * */
  _setup() {
    this.overlay = this._createOverlay();
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.showButton.forEach(button => {
      button.addEventListener('click', this.show);
    });

    this.closeButton.forEach(button => {
      button.addEventListener('click', this.hide);
    });

    this.elementRef.addEventListener(customEvent.CLOSE_MODAL, this.hide);
    this.elementRef.addEventListener(customEvent.SHOW_MODAL, this.show);

    this.overlay && this.overlay.addEventListener('click', this.overlayClickHandler);
  }

  /**
   * Returns the button to open the modal.
   *  - Immediate sibling of `this.elementRef` that has
   *      `data-toggle=modal` and `data-target=#[ID_OF_MODAL]`
   *
   * @return {Element} - HTML node that opens the modal.
   * */
  _getOpenButton() {
    return document.querySelectorAll(`[data-toggle="${this.className['MODAL']}"]`).filter(elem => {
      return (elem.dataset.target === `#${this.elementRef.id}`);
    });
  }

  /**
   * Returns the button to close the modal.
   *  - First children of `this.elementRef` that has `data-hide='modal'`
   *
   * @return {Element} - HTML node that closes the modal.
   * */
  _getCloseButton() {
    return this.elementRef.querySelectorAll("[data-hide='modal']");
  }

  /**
   * Creates the overlay for the modal.
   *
   * @return {Element} - HTML node for the overlay.
   * */
  _createOverlay() {
    let overlay = document.querySelector(`.${this.className['OVERLAY']}`);

    if (overlay) { return null; }

    overlay = document.createElement('div');
    overlay.setAttribute('class', this.className['OVERLAY']);
    document.body.appendChild(overlay);

    return overlay;
  }
}

export default Modal;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/modal/index.js